// ============================================================
// FirstLab.jsx
//
// NEW lesson page — the hands-on capstone lab linked from the
// "Start First Lab" button on EmbeddedSystemsTutorials.jsx (that
// button previously did nothing; see the note at the bottom of
// this file for the route you'll need to add).
//
// Builds the Smart RFID Lock project end-to-end, in the browser:
//   1) a parts list with real prices (the "what do I buy" step),
//   2) a wiring diagram that updates when you choose the actuator
//      type — the "how do I wire it up" step,
//   3) a simulated compile-and-upload console, like Arduino IDE's
//      output — the "how do I load the software" step,
//   4) a live, interactive circuit simulation (tap an authorized or
//      unknown card and watch the LCD/LED/buzzer/servo react) so the
//      lab has a real feel without requiring physical hardware,
//   5) an "Open a Real Simulator" link to Wokwi — a genuine,
//      currently free, browser-based Arduino/ESP32 simulator — for
//      students who want to run the *actual* sketch below against a
//      real compiler, not just this page's animation.
//
// A note on tool accuracy: this page deliberately does NOT recommend
// NI Multisim Live. That product reached end-of-life on September
// 15, 2026 and is no longer available — a prior draft of the
// companion Power Electronics lesson would have pointed students at
// a dead product. Wokwi and Tinkercad Circuits are both currently
// free and active; PSpice for TI (Cadence, sponsored by Texas
// Instruments) is a free, currently-available SPICE tool and is used
// in the Power Electronics & Circuit Simulation lesson instead.
// ============================================================

import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Paper,
    Stack,
    IconButton,
    Avatar,
    Radio,
    RadioGroup,
    FormControlLabel,
    Button,
    LinearProgress,
} from "@mui/material";

// ── Icons — @mui/icons-material only ──
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import MemoryIcon from "@mui/icons-material/Memory";
import NfcIcon from "@mui/icons-material/Nfc";
import SettingsInputComponentIcon from "@mui/icons-material/SettingsInputComponent";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import DvrIcon from "@mui/icons-material/Dvr";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import UploadIcon from "@mui/icons-material/Upload";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockIcon from "@mui/icons-material/Lock";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import TerminalIcon from "@mui/icons-material/Terminal";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

// ── Brand tokens (matches IoTDashboard.jsx / SensorsActuators.jsx) ──
const BRAND = {
    bg: "#100720",
    surface: "rgba(16,7,32,0.85)",
    border: "rgba(255,255,255,0.1)",
    gold: "goldenrod",
    teal: "#2DD4BF",
    white: "#FAFAF9",
    muted: "rgba(255,255,255,0.65)",
    codeBg: "#0b0515",
    danger: "#F87171",
};

const REQUIRED_PARTS = [
    { icon: <MemoryIcon />, name: "Arduino Uno (or Nano)", role: "Brain of the lock", price: "$4 – $12" },
    { icon: <NfcIcon />, name: "MFRC522 RFID reader + tag/card", role: "Reads the card's UID over SPI", price: "$2 – $5" },
    { icon: <DvrIcon />, name: "16×2 LCD with I2C backpack", role: "Shows status to the user", price: "$3 – $6" },
    { icon: <VolumeUpIcon />, name: "Active buzzer", role: "Beeps on denied access", price: "$0.50 – $1.50" },
    { icon: <LightbulbIcon />, name: "LED + 220Ω resistor", role: "Status indicator", price: "$0.10 – $0.30" },
    { icon: <SettingsInputComponentIcon />, name: "Breadboard + jumper wires", role: "Ties it all together", price: "$3 – $6" },
];

const ACTUATOR_OPTIONS = {
    servo: {
        label: "SG90 Micro Servo",
        price: "$2 – $4",
        pin: "D6",
        detail: "Direct signal wire, no driver circuit needed — the simplest way to move a bolt or latch.",
    },
    motor: {
        label: "DC Motor + Relay Module",
        price: "$3 – $7",
        pin: "D6 (via relay IN)",
        detail: "The Arduino pin can't drive a motor directly — it switches a relay, which switches the motor's own power supply.",
    },
};

// A complete, compiling sketch: real MFRC522 + Servo + LiquidCrystal_I2C
// APIs, proper setup()/loop(), and no undeclared globals — the same
// standard this entire course holds every other code example to.
const buildSketch = (actuator) => `#include <SPI.h>
#include <MFRC522.h>
${actuator === "servo" ? "#include <Servo.h>" : ""}
#include <Wire.h>
#include <LiquidCrystal_I2C.h>

#define RST_PIN 9
#define SS_PIN  10
#define ACTUATOR_PIN 6
#define BUZZER_PIN 5
#define LED_PIN 4

MFRC522 rfid(SS_PIN, RST_PIN);
${actuator === "servo" ? "Servo lockActuator;" : "// D6 drives the relay module's IN pin, which switches the motor's own supply."}
LiquidCrystal_I2C lcd(0x27, 16, 2); // common address — run an I2C scanner if the display stays blank

// Read your own card's UID first (Serial.print each byte in HEX)
// and replace these four bytes with it.
byte authorizedUID[4] = {0xDE, 0xAD, 0xBE, 0xEF};

void setup() {
  Serial.begin(9600);
  SPI.begin();
  rfid.PCD_Init();

${actuator === "servo"
    ? "  lockActuator.attach(ACTUATOR_PIN);\n  lockActuator.write(0); // locked position"
    : "  pinMode(ACTUATOR_PIN, OUTPUT);\n  digitalWrite(ACTUATOR_PIN, LOW); // relay off = locked"}
  pinMode(BUZZER_PIN, OUTPUT);
  pinMode(LED_PIN, OUTPUT);

  lcd.init();
  lcd.backlight();
  lcd.print("Scan your card");
}

bool isAuthorized() {
  if (rfid.uid.size != 4) return false;
  for (byte i = 0; i < 4; i++) {
    if (rfid.uid.uidByte[i] != authorizedUID[i]) return false;
  }
  return true;
}

void grantAccess() {
  lcd.clear();
  lcd.print("ACCESS GRANTED");
  digitalWrite(LED_PIN, HIGH);
${actuator === "servo"
    ? "  lockActuator.write(90); // unlock\n  delay(3000);\n  lockActuator.write(0);  // re-lock"
    : "  digitalWrite(ACTUATOR_PIN, HIGH); // unlock\n  delay(3000);\n  digitalWrite(ACTUATOR_PIN, LOW);  // re-lock"}
  digitalWrite(LED_PIN, LOW);
}

void denyAccess() {
  lcd.clear();
  lcd.print("ACCESS DENIED");
  for (byte i = 0; i < 3; i++) {
    digitalWrite(BUZZER_PIN, HIGH);
    delay(150);
    digitalWrite(BUZZER_PIN, LOW);
    delay(150);
  }
}

void loop() {
  if (!rfid.PICC_IsNewCardPresent() || !rfid.PICC_ReadCardSerial()) {
    return;
  }

  if (isAuthorized()) {
    grantAccess();
  } else {
    denyAccess();
  }

  lcd.clear();
  lcd.print("Scan your card");
  rfid.PICC_HaltA();
  rfid.PCD_StopCrypto1();
}`;

const BUILD_LOG = [
    "Compiling sketch_rfid_lock.ino...",
    "Sketch uses 7,842 bytes (24%) of program storage space.",
    "Global variables use 412 bytes (20%) of dynamic memory.",
    "Uploading via /dev/ttyUSB0 (115200 baud)...",
    "avrdude: writing flash (7,842 bytes)...",
    "avrdude: verifying flash... done.",
    "Upload complete. Board running.",
];

// Sub-components (arrow-function style, defined before first use)

const PartRow = ({ icon, name, role, price }) => (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 1 }}>
        <Avatar sx={{ bgcolor: "rgba(218,165,32,0.15)", color: BRAND.gold, width: 36, height: 36 }}>{icon}</Avatar>
        <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ color: BRAND.white, fontWeight: 700 }}>{name}</Typography>
            <Typography variant="caption" sx={{ color: BRAND.muted }}>{role}</Typography>
        </Box>
        <Chip size="small" label={price} sx={{ bgcolor: "rgba(45,212,191,0.12)", color: BRAND.teal, fontWeight: 700 }} />
    </Stack>
);

const CodeTerminal = ({ code, label }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard API isn't available in every browser/context.
        }
    };

    return (
        <Box sx={{ background: BRAND.codeBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", background: "rgba(255,255,255,0.03)" }}>
                <Typography variant="caption" sx={{ color: BRAND.gold, fontWeight: 700, fontFamily: "monospace" }}>{label}</Typography>
                <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Box sx={{ p: 2.5, overflowX: "auto", maxHeight: 420 }}>
                <Typography component="pre" sx={{ fontFamily: "'Fira Code', monospace", fontSize: 12.5, color: "#FCD34D", lineHeight: 1.5 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
};

// A schematic-style wiring diagram, not a literal breadboard photo —
// it updates the actuator node/pin label based on the radio choice.
const WiringDiagram = ({ actuator }) => {
    const nodes = [
        { key: "rfid", label: "MFRC522", pin: "SPI · D9–D13", x: 40, y: 40 },
        { key: "lcd", label: "LCD (I2C)", pin: "A4 / A5", x: 360, y: 40 },
        { key: "actuator", label: ACTUATOR_OPTIONS[actuator].label, pin: ACTUATOR_OPTIONS[actuator].pin, x: 40, y: 220 },
        { key: "buzzer", label: "Buzzer", pin: "D5", x: 215, y: 260 },
        { key: "led", label: "LED", pin: "D4", x: 360, y: 220 },
    ];
    const center = { x: 200, y: 140 };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 3, p: 2, overflowX: "auto" }}>
            <svg viewBox="0 0 440 300" width="100%" height="280" style={{ minWidth: 380 }}>
                {nodes.map((n) => (
                    <line
                        key={`line-${n.key}`}
                        x1={center.x}
                        y1={center.y}
                        x2={n.x + 55}
                        y2={n.y + 20}
                        stroke={BRAND.teal}
                        strokeWidth="1.5"
                        strokeDasharray="4 3"
                    />
                ))}
                {/* Arduino Uno — the center node */}
                <rect x={center.x - 45} y={center.y - 25} width="90" height="50" rx="8" fill="rgba(218,165,32,0.15)" stroke={BRAND.gold} strokeWidth="1.5" />
                <text x={center.x} y={center.y + 5} textAnchor="middle" fill={BRAND.white} fontSize="12" fontWeight="700">Arduino Uno</text>

                {nodes.map((n) => (
                    <g key={n.key}>
                        <rect x={n.x} y={n.y} width="110" height="40" rx="6" fill={BRAND.surface} stroke={BRAND.border} />
                        <text x={n.x + 55} y={n.y + 16} textAnchor="middle" fill={BRAND.white} fontSize="11" fontWeight="700">{n.label}</text>
                        <text x={n.x + 55} y={n.y + 30} textAnchor="middle" fill={BRAND.teal} fontSize="10">{n.pin}</text>
                    </g>
                ))}
            </svg>
        </Box>
    );
};

// ── Main Page ───────────────────────────────────────────────────

const FirstLab = () => {
    const navigate = useNavigate();
    const [actuator, setActuator] = useState("servo");
    const [stage, setStage] = useState("idle"); // idle | building | ready | running
    const [logLines, setLogLines] = useState([]);
    const [cardStatus, setCardStatus] = useState(null); // null | "granted" | "denied"
    const timers = useRef([]);

    const runBuildSequence = () => {
        timers.current.forEach(clearTimeout);
        timers.current = [];
        setStage("building");
        setLogLines([]);
        setCardStatus(null);

        BUILD_LOG.forEach((line, i) => {
            const t = setTimeout(() => {
                setLogLines((prev) => [...prev, line]);
                if (i === BUILD_LOG.length - 1) setStage("ready");
            }, (i + 1) * 500);
            timers.current.push(t);
        });
    };

    const tapCard = (status) => {
        setStage("running");
        setCardStatus(status);
    };

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
            <Container maxWidth="lg">
                {/* HERO SECTION */}
                <Box sx={{ mb: 8, textAlign: { xs: "center", md: "left" } }}>
                    <Chip
                        icon={<SchoolIcon style={{ color: BRAND.gold }} />}
                        label="Embedded Engineering · First Lab"
                        sx={{ bgcolor: "rgba(218,165,32,0.1)", color: BRAND.gold, border: `1px solid ${BRAND.border}`, mb: 2 }}
                    />
                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Build the <span style={{ color: BRAND.teal }}>Smart RFID Lock</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                        Pick your parts, see how they wire to the board, load the firmware, and watch it run —
                        right here, before you ever touch a soldering iron.
                    </Typography>
                </Box>

                <Grid container spacing={5}>
                    {/* LEFT: parts + actuator choice */}
                    <Grid item xs={12} md={5}>
                        <Paper elevation={0} sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 4, mb: 3 }}>
                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                                <ShoppingCartIcon sx={{ color: BRAND.gold }} />
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>1. What You'll Buy</Typography>
                            </Stack>
                            {REQUIRED_PARTS.map((p) => (
                                <PartRow key={p.name} {...p} />
                            ))}
                        </Paper>

                        <Paper elevation={0} sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 4 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Choose Your Actuator</Typography>
                            <Typography variant="caption" sx={{ color: BRAND.muted }}>
                                Both unlock the door — they just drive it differently. Your choice changes the
                                wiring diagram and the sketch on the right.
                            </Typography>
                            <RadioGroup value={actuator} onChange={(e) => setActuator(e.target.value)} sx={{ mt: 1 }}>
                                {Object.entries(ACTUATOR_OPTIONS).map(([key, opt]) => (
                                    <FormControlLabel
                                        key={key}
                                        value={key}
                                        control={<Radio sx={{ color: BRAND.muted, "&.Mui-checked": { color: BRAND.gold } }} />}
                                        label={
                                            <Box sx={{ py: 0.5 }}>
                                                <Typography variant="body2" sx={{ color: BRAND.white, fontWeight: 700 }}>
                                                    {opt.label} <span style={{ color: BRAND.teal }}>· {opt.price}</span>
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: BRAND.muted }}>{opt.detail}</Typography>
                                            </Box>
                                        }
                                    />
                                ))}
                            </RadioGroup>
                        </Paper>
                    </Grid>

                    {/* RIGHT: wiring, code, build + run */}
                    <Grid item xs={12} md={7}>
                        <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>2. Wire It Up</Typography>
                        <WiringDiagram actuator={actuator} />

                        <Typography variant="h6" sx={{ fontWeight: 800, mt: 4, mb: 2 }}>3. Load the Software</Typography>
                        <CodeTerminal label="rfid_lock.ino" code={buildSketch(actuator)} />

                        <Button
                            variant="contained"
                            startIcon={<UploadIcon />}
                            onClick={runBuildSequence}
                            disabled={stage === "building"}
                            sx={{ bgcolor: BRAND.gold, color: BRAND.bg, fontWeight: 800, mb: 2 }}
                        >
                            Compile & Upload
                        </Button>

                        {stage === "building" && (
                            <Box sx={{ mb: 2 }}>
                                <LinearProgress sx={{ mb: 1, "& .MuiLinearProgress-bar": { bgcolor: BRAND.teal }, bgcolor: "rgba(255,255,255,0.08)" }} />
                            </Box>
                        )}

                        {logLines.length > 0 && (
                            <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 3, p: 2, mb: 4 }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                    <TerminalIcon sx={{ fontSize: 16, color: BRAND.teal }} />
                                    <Typography variant="caption" sx={{ color: BRAND.teal, fontFamily: "monospace" }}>build output</Typography>
                                </Stack>
                                {logLines.map((line, i) => (
                                    <Typography key={i} sx={{ fontFamily: "'Fira Code', monospace", fontSize: 12.5, color: BRAND.muted }}>
                                        {line}
                                    </Typography>
                                ))}
                            </Box>
                        )}

                        {stage === "ready" || stage === "running" ? (
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>4. Run It</Typography>
                                <Paper elevation={0} sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 4 }}>
                                    {/* Simulated LCD */}
                                    <Box
                                        sx={{
                                            background: "#0b2e13",
                                            border: "2px solid #1f3d24",
                                            borderRadius: 1,
                                            p: 2,
                                            mb: 3,
                                            fontFamily: "monospace",
                                            textAlign: "center",
                                        }}
                                    >
                                        <Typography sx={{ color: "#8BE28B", fontFamily: "monospace", fontSize: 16, letterSpacing: 1 }}>
                                            {cardStatus === "granted" && "ACCESS GRANTED"}
                                            {cardStatus === "denied" && "ACCESS DENIED"}
                                            {!cardStatus && "SCAN YOUR CARD"}
                                        </Typography>
                                    </Box>

                                    <Grid container spacing={2} sx={{ mb: 3 }} justifyContent="center">
                                        <Grid item>
                                            <Stack alignItems="center" spacing={0.5}>
                                                <Avatar sx={{ bgcolor: cardStatus === "granted" ? BRAND.teal : "rgba(255,255,255,0.08)", color: BRAND.bg, transition: "0.3s" }}>
                                                    <LightbulbIcon />
                                                </Avatar>
                                                <Typography variant="caption" sx={{ color: BRAND.muted }}>LED</Typography>
                                            </Stack>
                                        </Grid>
                                        <Grid item>
                                            <Stack alignItems="center" spacing={0.5}>
                                                <Avatar
                                                    sx={{
                                                        bgcolor: cardStatus === "denied" ? BRAND.danger : "rgba(255,255,255,0.08)",
                                                        color: BRAND.bg,
                                                        transition: "0.3s",
                                                        animation: cardStatus === "denied" ? "pulse 0.3s ease-in-out 3" : "none",
                                                        "@keyframes pulse": { "0%,100%": { transform: "scale(1)" }, "50%": { transform: "scale(1.15)" } },
                                                    }}
                                                >
                                                    <VolumeUpIcon />
                                                </Avatar>
                                                <Typography variant="caption" sx={{ color: BRAND.muted }}>Buzzer</Typography>
                                            </Stack>
                                        </Grid>
                                        <Grid item>
                                            <Stack alignItems="center" spacing={0.5}>
                                                <Avatar
                                                    sx={{
                                                        bgcolor: cardStatus === "granted" ? BRAND.gold : "rgba(255,255,255,0.08)",
                                                        color: BRAND.bg,
                                                        transition: "transform 0.4s",
                                                        transform: cardStatus === "granted" ? "rotate(90deg)" : "rotate(0deg)",
                                                    }}
                                                >
                                                    {cardStatus === "granted" ? <LockOpenIcon /> : <LockIcon />}
                                                </Avatar>
                                                <Typography variant="caption" sx={{ color: BRAND.muted }}>
                                                    {ACTUATOR_OPTIONS[actuator].label.split(" ")[0]}
                                                </Typography>
                                            </Stack>
                                        </Grid>
                                    </Grid>

                                    <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
                                        <Button
                                            variant="outlined"
                                            startIcon={<CheckCircleIcon />}
                                            onClick={() => tapCard("granted")}
                                            sx={{ borderColor: BRAND.teal, color: BRAND.teal }}
                                        >
                                            Tap Authorized Card
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={<CancelIcon />}
                                            onClick={() => tapCard("denied")}
                                            sx={{ borderColor: BRAND.danger, color: BRAND.danger }}
                                        >
                                            Tap Unknown Card
                                        </Button>
                                    </Stack>
                                </Paper>
                            </Box>
                        ) : null}
                    </Grid>
                </Grid>

                {/* REAL SIMULATOR LINK */}
                <Box sx={{ mt: 8, p: 5, borderRadius: 6, background: "rgba(255,255,255,0.03)", border: `1px solid ${BRAND.border}`, textAlign: "center" }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Want a Real Compiler, Not Just an Animation?</Typography>
                    <Typography sx={{ color: BRAND.muted, mb: 3, maxWidth: 640, mx: "auto" }}>
                        The simulation above is for building intuition fast. When you're ready to run the
                        actual sketch through a real AVR compiler, Wokwi is a free, browser-based Arduino/ESP32
                        simulator — paste the code above into a new Arduino Uno project.
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                        <Button
                            variant="contained"
                            endIcon={<OpenInNewIcon />}
                            component="a"
                            href="https://wokwi.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ bgcolor: BRAND.teal, color: BRAND.bg, fontWeight: 800 }}
                        >
                            Open Wokwi
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => navigate("/power-electronics-simulation")}
                            sx={{ borderColor: BRAND.gold, color: BRAND.gold, fontWeight: 800 }}
                        >
                            Next: Power Electronics & Simulation
                        </Button>
                    </Stack>
                </Box>
            </Container>
        </Box>
    );
};

export default FirstLab;

// ------------------------------------------------------------------
// ACTION ITEM for Hazzan: this is a new standalone page with no
// route yet. Add to App.js, e.g.:
//   import FirstLab from "./pages/FirstLab";
//   <Route path="/embedded-first-lab" element={<RequireAuth user={user}><FirstLab/></RequireAuth>}/>
// and point EmbeddedSystemsTutorials.jsx's navigate("/embedded-first-lab")
// (see that file's "Start First Lab" button) at whatever path you choose.
// ------------------------------------------------------------------
